// src/pages/formui.js
// --- Geteilte UI-Helper fuer Form-Komponenten --------------------------------

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { t } from '../lib/sprache';

// Hilfs-Hook fuer responsive Layout (Mobile-Detection)
export function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

export const C = {
  navy: '#0A1628', navyMid: '#112240', navyLight: '#1A3357',
  yellow: '#F2B705', yellowBg: '#FEF6DC',
  white: '#FFFFFF', bg: '#F0F3F8',
  gray100: '#EAEEF5', gray200: '#D4DCE8', gray300: '#B0BDD0',
  gray500: '#6B7A95', gray700: '#384560',
  green: '#1A7A4A', greenBg: '#E5F4ED',
  red: '#C0392B', redBg: '#FDECEA',
  orange: '#D97706', orangeBg: '#FEF3DC',
  // Orange als SCHRIFT ist auf Weiss zu blass (Kontrast 3:1) - dafuer dieses.
  orangeText: '#9A6500',
  // Heller Text auf dem dunklen Kopf (vorher mehrfach fest eingetragen).
  onDark: '#B7C4D2',
  blue: '#1565C0', blueBg: '#E3EDFB',
};

// Einheitliche Schriftgroessen (Design-Pruefung 15.09.2026): vorher 18
// verschiedene Werte. xs Schilder/Zaehler, s Hinweise, m Text/Knoepfe,
// l Pruefpunkt-Frage und Kartentitel, xl Seitentitel.
export const S = { xs: 13, s: 14, m: 16, l: 18, xl: 22 };

// Strich-Symbole im Stil der unteren Leiste (statt Platzhaltern wie "S", "x",
// "*" oder ">"). pfad: SVG-Pfad auf 24x24.
export const SYMBOL = {
  suche: 'M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14z M20 20l-4-4',
  schliessen: 'M18 6 6 18 M6 6l12 12',
  weiter: 'M9 18l6-6-6-6',
  station: 'M13 2 4 14h7l-1 8 9-12h-7z',
};
export function Icon({ pfad, groesse = 20, farbe = 'currentColor', staerke = 2 }) {
  return React.createElement('svg', {
    width: groesse, height: groesse, viewBox: '0 0 24 24', fill: 'none', stroke: farbe,
    strokeWidth: staerke, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
    style: { display: 'block', flexShrink: 0 },
  }, React.createElement('path', { d: pfad }));
}

// Zurueck-Knopf im dunklen Seitenkopf - vorher in 8 Dateien unterschiedlich,
// nur ca. 30 px hoch und mal "< Dashboard", mal "< Zurück".
export const zurueckStil = {
  minHeight: 44, padding: '8px 14px', background: 'none',
  border: '1.5px solid ' + C.navyLight, color: C.white, borderRadius: 8,
  cursor: 'pointer', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap',
};

// Schriftgroessen (Stand 01.09.2026, fuer Monteure 40+ auf dem Handy):
//   Eingabefelder MUESSEN mindestens 16 px haben. Safari auf dem iPhone zoomt
//   sonst beim Antippen automatisch in die Seite hinein - die Ansicht springt.
//   Das ist keine Geschmacksfrage, sondern festes Verhalten von iOS.
//   Beschriftungen 15, Hinweise 14 (siehe S weiter oben).
export const inputStyle = {
  width: '100%', padding: '12px 12px', borderRadius: 7,
  border: `1.5px solid ${C.gray200}`, fontSize: 16, outline: 'none',
  boxSizing: 'border-box', background: C.white, fontFamily: 'inherit',
  minHeight: 48,
};

export function Field({ label, children, hint, required }) {
  return React.createElement('div', { style: { marginBottom: 14 } },
    React.createElement('div', { style: { fontSize: 15, fontWeight: 700, color: C.gray700, marginBottom: 5 } },
      label, ' ', required && React.createElement('span', { style: { color: C.red } }, '*')
    ),
    children,
    hint && React.createElement('div', { style: { fontSize: 14, color: C.gray700, marginTop: 4 } }, hint)
  );
}

export function TextInput({ value, onChange, placeholder, type = 'text' }) {
  return React.createElement('input', {
    type, value: value || '',
    onChange: e => onChange(e.target.value),
    placeholder, style: inputStyle,
  });
}

export function NumberInput({ value, onChange, placeholder, step }) {
  return React.createElement('input', {
    type: 'number', value: value ?? '', step,
    onChange: e => onChange(e.target.value === '' ? null : Number(e.target.value)),
    placeholder, style: inputStyle,
  });
}

// Eingabefeld mit Vorschlagsliste.
//
// Frueher ein <datalist>. Das kennt Safari auf dem iPhone GAR NICHT - dort war
// es einfach ein leeres Textfeld ohne jede Vorauswahl, und auf Android nur
// halb zuverlaessig. Genau deshalb ging die Vorauswahl "nur am PC".
// Jetzt eine eigene Liste aus Knoepfen: funktioniert ueberall, 46 px hoch,
// mit dem Daumen zu treffen.
// Bezeichnung der Station: Vorschlaege zum Antippen, danach frei ergaenzbar
// (Anna-Lena 24.09.2026: "waehlt Unterstation und ergaenzt Sued, 1 o. ae.").
export const STATION_BEZEICHNUNGEN = ['Übergabestation', 'Unterstation'];

// Den vorderen Teil austauschen und den Zusatz behalten:
// "Unterstation Süd" + Übergabestation -> "Übergabestation Süd".
export function bezeichnungMitTyp(alt, typ) {
  let rest = String(alt || '').trim();
  for (const p of STATION_BEZEICHNUNGEN) {
    if (rest.toLowerCase().startsWith(p.toLowerCase())) { rest = rest.slice(p.length).trim(); break; }
  }
  if (!typ) return rest;
  return rest ? typ + ' ' + rest : typ;
}
export function typDerBezeichnung(wert) {
  const w = String(wert || '').trim().toLowerCase();
  return STATION_BEZEICHNUNGEN.find(p => w.startsWith(p.toLowerCase())) || (w ? 'sonstige' : null);
}

export function BezeichnungVorschlaege({ value, onChange }) {
  const aktiv = typDerBezeichnung(value);
  const knopf = (beschriftung, typ) => React.createElement('button', {
    key: beschriftung,
    type: 'button',
    onClick: () => onChange(bezeichnungMitTyp(value, typ)),
    style: {
      minHeight: 40, padding: '7px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
      fontSize: 14, fontWeight: 700,
      border: '1.5px solid ' + (aktiv === (typ || 'sonstige') ? C.navy : C.gray200),
      background: aktiv === (typ || 'sonstige') ? C.navy : C.white,
      color: aktiv === (typ || 'sonstige') ? C.white : C.gray700,
    },
  }, t(beschriftung));
  return React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 } },
    STATION_BEZEICHNUNGEN.map(p => knopf(p, p)),
    knopf('sonstige', null));
}

export function ComboInput({ value, onChange, options, placeholder }) {
  const [offen, setOffen] = useState(false);
  const huelle = useRef(null);

  // Klick ausserhalb schliesst die Liste. Auf dem Handy zaehlt auch ein Tippen
  // irgendwo anders als Klick.
  useEffect(() => {
    if (!offen) return;
    const zu = (e) => { if (huelle.current && !huelle.current.contains(e.target)) setOffen(false); };
    document.addEventListener('mousedown', zu);
    document.addEventListener('touchstart', zu);
    return () => {
      document.removeEventListener('mousedown', zu);
      document.removeEventListener('touchstart', zu);
    };
  }, [offen]);

  const text = value == null ? '' : String(value);
  const passend = (options || []).filter(o =>
    !text || String(o).toLowerCase().includes(text.toLowerCase()));

  return React.createElement('div', { ref: huelle, style: { position: 'relative' } },
    React.createElement('input', {
      type: 'text',
      value: text,
      onChange: e => { onChange(e.target.value); setOffen(true); },
      onFocus: () => setOffen(true),
      placeholder,
      style: { ...inputStyle, paddingRight: 40 },
    }),
    // Pfeil zum Aufklappen - auch wenn schon etwas drinsteht.
    React.createElement('button', {
      type: 'button',
      onClick: () => setOffen(o => !o),
      'aria-label': t('Liste öffnen'),
      style: {
        position: 'absolute', right: 2, top: 2, bottom: 2, width: 36,
        background: 'none', border: 'none', cursor: 'pointer',
        color: C.gray500, fontSize: 15, lineHeight: 1, fontFamily: 'inherit',
      },
    }, offen ? '▲' : '▼'),
    offen && passend.length > 0 && React.createElement('div', {
      style: {
        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60,
        background: C.white, border: '1.5px solid ' + C.gray200, borderRadius: 8,
        marginTop: 4, maxHeight: 260, overflowY: 'auto',
        boxShadow: '0 8px 24px rgba(10,22,40,0.18)',
      },
    },
      passend.map(o => React.createElement('button', {
        key: o,
        type: 'button',
        onMouseDown: (e) => e.preventDefault(),   // Feld behaelt den Fokus
        onClick: () => { onChange(String(o)); setOffen(false); },
        style: {
          display: 'block', width: '100%', textAlign: 'left',
          padding: '13px 14px', minHeight: 46, border: 'none',
          borderBottom: '1px solid ' + C.gray100,
          background: String(o) === text ? C.greenBg : C.white,
          color: C.navy, fontSize: 16, cursor: 'pointer', fontFamily: 'inherit',
        },
      }, String(o)))
    )
  );
}

export function Select({ value, onChange, options, placeholder }) {
  return React.createElement('select', {
    value: value || '',
    onChange: e => onChange(e.target.value || null),
    style: inputStyle,
  },
    React.createElement('option', { value: '' }, placeholder || t('-- bitte wählen --')),
    options.map(o => React.createElement('option', { key: o.value, value: o.value }, o.label))
  );
}

// Plausibilitaets-Hinweis: fragt nach, verbietet aber nichts.
// Bewusst kein Blockieren - die Wirklichkeit vor Ort haelt sich nicht an
// unsere Annahmen, und ein Monteur, der einen Sonderfall nicht eintragen kann,
// traegt am Ende gar nichts ein.
export function PlausiHinweis({ text }) {
  if (!text) return null;
  return React.createElement('div', {
    style: {
      marginTop: 5, padding: '7px 10px', background: C.orangeBg,
      borderLeft: '3px solid ' + C.orange, borderRadius: 5,
      fontSize: 14, color: C.gray700, lineHeight: 1.4,
    },
  }, '⚠ ' + t(text));
}

// Die Pruefungen selbst. Geben einen Hinweistext zurueck oder null.
export const plausi = {
  herstellungsjahr(v) {
    if (v == null || v === '') return null;
    const jahr = Number(v);
    const jetzt = new Date().getFullYear();
    if (!isFinite(jahr)) return null;
    if (jahr > jetzt) return t('Baujahr liegt in der Zukunft. Vertippt?');
    if (jahr < 1900) return t('Baujahr vor 1900 - bitte prüfen.');
    // Excel-Datumszahlen wie 45231 sind uns beim Altdaten-Import begegnet.
    if (jahr > 3000) return t('Das sieht nach einem Datum aus, nicht nach einem Jahr.');
    return null;
  },
  leistungKva(v) {
    if (v == null || v === '') return null;
    const kva = Number(v);
    if (!isFinite(kva) || kva <= 0) return null;
    if (kva < 50) return t('Unter 50 kVA ist für eine Trafostation ungewöhnlich. Kilowatt statt kVA eingetragen?');
    if (kva > 4000) return t('Über 4000 kVA ist selten. Bitte kurz prüfen.');
    return null;
  },
  spannungKv(v) {
    if (v == null || v === '') return null;
    const kv = Number(v);
    if (!isFinite(kv) || kv <= 0) return null;
    if (kv > 60) return t('Über 60 kV - ist der Wert in Volt statt Kilovolt eingetragen?');
    if (kv < 1) return t('Unter 1 kV ist Niederspannung. Gehört der Wert hierher?');
    return null;
  },
  ohm(v) {
    if (v == null || String(v).trim() === '') return null;
    const n = parseFloat(String(v).replace(',', '.'));
    if (!isFinite(n)) return null;
    if (n > 100) return t('Über 100 Ω ist für eine Erdung sehr hoch. Messgerät oder Komma prüfen.');
    return null;
  },
};

export function Checkbox({ checked, onChange, label }) {
  return React.createElement('label', {
    // Groessere Kaestchen und mehr Hoehe: mit Handschuhen war 18 px zu klein.
    style: { display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '10px 0', minHeight: 44 },
  },
    React.createElement('input', {
      type: 'checkbox', checked: !!checked,
      onChange: e => onChange(e.target.checked),
      style: { width: 26, height: 26, cursor: 'pointer', flexShrink: 0 },
    }),
    React.createElement('span', { style: { fontSize: 15, color: C.navy, lineHeight: 1.4 } }, label)
  );
}

export function TriToggle({ value, onChange, optionA, optionB }) {
  const btnA = {
    flex: 1, padding: '13px 12px', borderRadius: 7,
    background: value === 'a' ? C.green : C.white,
    color: value === 'a' ? C.white : C.gray700,
    border: '1.5px solid ' + (value === 'a' ? C.green : C.gray200),
    cursor: 'pointer', fontSize: 15, fontWeight: 700,
  };
  const btnB = {
    flex: 1, padding: '13px 12px', borderRadius: 7,
    background: value === 'b' ? C.red : C.white,
    color: value === 'b' ? C.white : C.gray700,
    border: '1.5px solid ' + (value === 'b' ? C.red : C.gray200),
    cursor: 'pointer', fontSize: 15, fontWeight: 700,
  };
  return React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 8 } },
    React.createElement('button', { type: 'button', onClick: () => onChange(value === 'a' ? null : 'a'), style: btnA }, optionA),
    React.createElement('button', { type: 'button', onClick: () => onChange(value === 'b' ? null : 'b'), style: btnB }, optionB)
  );
}

export function Row({ children, columns = 2 }) {
  // Auf Mobile (<640px) automatisch auf 1 Spalte stacken - Felder werden voll breit.
  const isMobile = useIsMobile(640);
  const cols = isMobile ? 1 : columns;
  return React.createElement('div', {
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(' + cols + ', 1fr)',
      gap: 12,
      alignItems: 'start', // 15.05.: Felder oben ausrichten, kein vertikaler Versatz mehr.
    },
  }, children);
}

export function Subsection({ title, children }) {
  return React.createElement('div', { style: { marginBottom: 18, paddingTop: 14, borderTop: '2px solid ' + C.gray100 } },
    React.createElement('div', {
      style: { fontWeight: 800, color: C.navy, fontSize: 14, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }
    }, title),
    children
  );
}

// ---------------------------------------------------------------------------
// Datum zum TIPPEN (Stand 13.09.2026)
// ---------------------------------------------------------------------------
// Aus dem Feldtest: Beim <input type="date"> muss der Monteur fuer ein Baujahr
// 1970 den Kalender 50 Jahre zurueckblaettern. Und oft kennt er nur Monat und
// Jahr oder nur das Jahr. Deshalb ein normales Textfeld mit Ziffern-Tastatur,
// die Punkte setzt die App selbst.
//
// Gespeichert wird immer ISO:  TT.MM.JJJJ -> 'JJJJ-MM-TT'
//                              MM.JJJJ    -> 'JJJJ-MM'
//                              JJJJ       -> 'JJJJ'
//                              leer       -> ''
// genauigkeit 'tag' laesst nur ein volles Datum zu, 'flexibel' alle drei.

// Gespeicherten Wert zerlegen. Nimmt 'JJJJ-MM-TT' (auch mit Uhrzeit dahinter),
// 'JJJJ-MM' und 'JJJJ'. Liefert null, wenn es keins davon ist.
function isoZerlegen(wert) {
  if (wert == null) return null;
  const m = String(wert).trim().match(/^(\d{4})(?:-(\d{1,2})(?:-(\d{1,2})(?:[T ].*)?)?)?$/);
  if (!m) return null;
  return { jahr: m[1], monat: m[2] || null, tag: m[3] || null };
}

const zweistellig = (s) => String(s).padStart(2, '0');

// Anzeige auf Deutsch: '1998-03-15' -> '15.03.1998', '1998-03' -> '03.1998',
// '1998' -> '1998'. Leer -> ''. Unbekanntes Format bleibt, wie es ist.
export function datumAnzeige(wert) {
  if (wert == null || wert === '') return '';
  const z = isoZerlegen(wert);
  if (!z) return String(wert);
  if (z.tag) return zweistellig(z.tag) + '.' + zweistellig(z.monat) + '.' + z.jahr;
  if (z.monat) return zweistellig(z.monat) + '.' + z.jahr;
  return z.jahr;
}

// Getippten Text pruefen. Liefert den ISO-Wert ('' fuer leer) oder null,
// wenn der Text kein gueltiges Datum ist.
function datumAusText(text, genauigkeit) {
  const t = String(text || '').trim().replace(/\.$/, '');   // '03.1998.' ist ok
  if (t === '') return '';
  let tag = null, monat = null, jahr = null;
  if (t.includes('.')) {
    const teile = t.split('.');
    if (teile.some(x => !/^\d+$/.test(x))) return null;
    if (teile.length === 3) [tag, monat, jahr] = teile;
    else if (teile.length === 2) [monat, jahr] = teile;
    else return null;
  } else {
    if (!/^\d+$/.test(t)) return null;
    if (t.length === 8) { tag = t.slice(0, 2); monat = t.slice(2, 4); jahr = t.slice(4); }
    else if (t.length === 6) { monat = t.slice(0, 2); jahr = t.slice(2); }
    else if (t.length === 4) { jahr = t; }
    else return null;
  }
  if (genauigkeit === 'tag' && tag == null) return null;
  // Jahr immer vierstellig - bei '98' wuesste niemand, ob 1998 oder 2098.
  if (!/^\d{4}$/.test(jahr)) return null;
  const j = Number(jahr);
  if (j < 1900 || j > new Date().getFullYear() + 30) return null;
  if (monat == null) return jahr;
  if (monat.length > 2) return null;
  const mo = Number(monat);
  if (mo < 1 || mo > 12) return null;
  if (tag == null) return jahr + '-' + zweistellig(mo);
  if (tag.length > 2) return null;
  const ta = Number(tag);
  const tageImMonat = new Date(j, mo, 0).getDate();
  if (ta < 1 || ta > tageImMonat) return null;
  return jahr + '-' + zweistellig(mo) + '-' + zweistellig(ta);
}

// Punkte automatisch setzen, nur nach der Zahl der Ziffern.
//   tag:      15 -> 15.03 -> 15.03.1998
//   flexibel: bis 4 Ziffern ohne Punkt (koennte ein Jahr sein),
//             5-6 Ziffern MM.JJJJ, 7-8 Ziffern TT.MM.JJJJ
function punkteSetzen(ziffern, genauigkeit) {
  const z = ziffern.slice(0, 8);
  const n = z.length;
  const tmj = () => n <= 2 ? z
    : n <= 4 ? z.slice(0, 2) + '.' + z.slice(2)
    : z.slice(0, 2) + '.' + z.slice(2, 4) + '.' + z.slice(4);
  if (genauigkeit === 'tag') return tmj();
  if (n <= 4) return z;
  if (n <= 6) return z.slice(0, 2) + '.' + z.slice(2);
  return tmj();
}

// Selbst getippte Punkte: nur Ziffern und Punkte, kein Punkt am Anfang,
// keine doppelten, hoechstens zwei. Komma, Strich, Schraegstrich und
// Leerzeichen gelten als Punkt (manche Handy-Tastaturen haben keinen Punkt).
function handTextBereinigen(roh) {
  let t = String(roh).replace(/[,\-/ ]/g, '.').replace(/[^\d.]/g, '')
    .replace(/\.{2,}/g, '.').replace(/^\./, '');
  const teile = t.split('.');
  if (teile.length > 3) t = teile.slice(0, 3).join('.') + teile.slice(3).join('');
  const t2 = t.split('.');
  // '15.031998' -> '15.03.1998': ein letzter Block mit mehr als 4 Ziffern
  // kann kein Jahr sein, also fehlt dort der Punkt nach dem Monat.
  while (t2.length < 3 && t2[t2.length - 1].length > 4) {
    const letzt = t2.pop();
    t2.push(letzt.slice(0, 2), letzt.slice(2));
  }
  if (t2.length === 3) t2[2] = t2[2].slice(0, 4);
  return t2.join('.').slice(0, 10);
}

const nurZiffern = (s) => String(s || '').replace(/\D/g, '');

export function DatumFeld({ value, onChange, genauigkeit = 'flexibel', placeholder }) {
  const wertText = value == null ? '' : String(value);
  const [text, setText] = useState(() => datumAnzeige(wertText));
  const [fehler, setFehler] = useState(false);
  // Letzter Wert, den wir selbst gemeldet (oder von aussen bekommen) haben.
  // Nur wenn value davon abweicht, kam die Aenderung von aussen - sonst
  // wuerde das Feld dem Monteur beim Tippen den Text umbauen.
  const letzterWert = useRef(wertText);
  const feld = useRef(null);
  const cursorNachZiffern = useRef(null);

  useEffect(() => {
    if (wertText !== letzterWert.current) {
      letzterWert.current = wertText;
      setText(datumAnzeige(wertText));
      setFehler(false);
    }
  }, [wertText]);

  // Nach dem automatischen Punkte-Setzen den Cursor hinter dieselbe Ziffer
  // stellen wie vorher - sonst springt er beim Korrigieren in der Mitte ans Ende.
  useLayoutEffect(() => {
    const n = cursorNachZiffern.current;
    cursorNachZiffern.current = null;
    const el = feld.current;
    if (n == null || !el || document.activeElement !== el) return;
    let pos = 0, gezaehlt = 0;
    while (pos < text.length && gezaehlt < n) {
      if (/\d/.test(text[pos])) gezaehlt++;
      pos++;
    }
    try { el.setSelectionRange(pos, pos); } catch (e) { /* egal */ }
  }, [text]);

  const melden = (iso) => {
    const schonGemeldet = letzterWert.current === iso;
    letzterWert.current = iso;
    // Nicht doppelt melden, wenn der Elternteil den Wert noch nicht zurueckgibt.
    if (iso !== wertText && !schonGemeldet) onChange(iso);
  };

  const beimTippen = (e) => {
    const roh = e.target.value;
    const alt = text;
    let neu;
    const punkteDazu = (roh.match(/\./g) || []).length > (alt.match(/\./g) || []).length
      || /[,\-/ ]/.test(roh);
    if (roh.length < alt.length) {
      // Loeschen: nichts umbauen, damit die Ruecktaste sich normal verhaelt.
      neu = handTextBereinigen(roh);
    } else if (!punkteDazu && alt === punkteSetzen(nurZiffern(alt), genauigkeit)) {
      // Bisher automatisch formatiert und nur Ziffern dazu -> Punkte neu setzen.
      neu = punkteSetzen(nurZiffern(roh), genauigkeit);
    } else {
      // Der Monteur setzt die Punkte selbst.
      neu = handTextBereinigen(roh);
    }
    if (neu !== roh) {
      const pos = e.target.selectionStart == null ? roh.length : e.target.selectionStart;
      cursorNachZiffern.current = nurZiffern(roh.slice(0, pos)).length;
    }
    setText(neu);
    setFehler(false);
    // Volles Datum (oder Monat.Jahr) gleich melden - schliesst der Monteur
    // danach die App, ist es trotzdem gespeichert. Ein reines Jahr erst beim
    // Verlassen: aus '1998' kann noch '19981' ... werden.
    const iso = datumAusText(neu, genauigkeit);
    if (iso && iso.length > 4) melden(iso);
  };

  const uebernehmen = () => {
    if (text === datumAnzeige(wertText)) { setFehler(false); return; }
    const iso = datumAusText(text, genauigkeit);
    if (iso == null) { setFehler(true); return; }
    setFehler(false);
    setText(datumAnzeige(iso));
    melden(iso);
  };

  const leeren = () => {
    setText('');
    setFehler(false);
    melden('');
  };

  const platzhalter = placeholder
    || (genauigkeit === 'tag' ? t('TT.MM.JJJJ') : t('TT.MM.JJJJ, MM.JJJJ oder JJJJ'));
  const hinweis = genauigkeit === 'tag'
    ? t('Datum bitte als TT.MM.JJJJ')
    : t('Datum bitte als TT.MM.JJJJ, MM.JJJJ oder JJJJ');
  const leer = text === '' && wertText === '';

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } },
      React.createElement('input', {
        ref: feld,
        type: 'text',
        inputMode: 'numeric',
        autoComplete: 'off',
        maxLength: 10,
        value: text,
        placeholder: platzhalter,
        onChange: beimTippen,
        onBlur: uebernehmen,
        onKeyDown: (e) => { if (e.key === 'Enter') { e.preventDefault(); uebernehmen(); } },
        style: {
          ...inputStyle, flex: '1 1 150px', minWidth: 140,
          borderColor: fehler ? C.red : C.gray200,
        },
      }),
      !leer && React.createElement('button', {
        type: 'button',
        onClick: leeren,
        title: t('Datum löschen'),
        style: {
          minHeight: 44, padding: '8px 14px', borderRadius: 8,
          border: '1.5px solid ' + C.gray200, background: C.white,
          color: C.gray700, fontWeight: 600, fontSize: 14, cursor: 'pointer',
          fontFamily: 'inherit', whiteSpace: 'nowrap',
        },
      }, t('leeren'))
    ),
    fehler && React.createElement('div', {
      style: { marginTop: 4, fontSize: 13.5, color: C.red, fontWeight: 600 },
    }, hinweis)
  );
}

// Datumsfeld mit Rückweg. Ein <input type="date"> lässt sich in mancher
// Browser-Version nicht mehr leeren, wenn einmal etwas drinsteht — und ein
// falsches Datum ist schlimmer als gar keins. Deshalb der kleine Knopf.
// Anna-Lena: "Datumsfelder zurücksetzen / nicht bekannt".
export function DateInput({ value, onChange, type = 'date', unbekanntText = t('nicht bekannt') }) {
  const leer = !value;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value || '')}
        style={{ ...inputStyle, flex: '1 1 150px', minWidth: 140 }}
      />
      {leer ? (
        <span style={{ fontSize: 14, color: C.gray500, fontStyle: 'italic' }}>{unbekanntText}</span>
      ) : (
        <button type="button" onClick={() => onChange('')}
          title={t('Datum löschen')}
          style={{ minHeight: 44, padding: '8px 14px', borderRadius: 8,
                   border: '1.5px solid ' + C.gray200, background: C.white,
                   color: C.gray700, fontWeight: 600, fontSize: 14, cursor: 'pointer',
                   fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
          {t('zurücksetzen')}
        </button>
      )}
    </div>
  );
}
